import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPanel from './NotificationPanel';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAuthenticated, signOut, openAuthModal } = useAuth();
  const { getCartItemsCount, openCart } = useCart();
  const { unreadCount, toggleOpen: toggleNotifications } = useNotifications();

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    console.log('[Header] Signing out...');
    try {
      const { error: signOutError } = await signOut();
      if (signOutError) {
        console.error('[Header] Sign out failed:', signOutError);
        alert('Sign out failed. Please try again.');
        return;
      }
    } catch (err) {
      console.error('[Header] Sign out exception:', err);
      alert('Sign out failed. Please try again.');
      return;
    }
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
    navigate('/caterer-discovery');
    // Hard refresh to clear any lingering client state/session cache
    setTimeout(() => window.location.reload(), 50);
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User';

  return (
    <>
      <header className="header-container">
        <div className="header-content">
          <Link to="/caterer-discovery" className="header-logo">
            <img src="/logo.png" alt="MrsCater" className="header-logo-img" style={{ height: '48px', width: 'auto' }} />
          </Link>

          {/* Auth Buttons / User Menu */}
          <div className="flex items-center gap-3 ml-auto">
            {/* Find Caterers Button - Always visible */}
            <Link
              to="/caterer-discovery"
              className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isActive('/caterer-discovery') 
                  ? 'bg-primary/10 text-primary' 
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="Search" size={18} />
              <span>Find Caterers</span>
            </Link>

            {/* Referral - Always visible */}
            <Link
              to="/referral"
              className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isActive('/referral') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
              }`}
              onClick={() => setShowUserMenu(false)}
            >
              <Icon name="Gift" size={18} />
              <span>Invite &amp; Earn</span>
            </Link>

            {/* Special Request Button - Always visible */}
            <Link
              to="/custom-request"
              className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isActive('/custom-request') 
                  ? 'bg-accent text-accent-foreground' 
                  : 'bg-accent/10 text-accent hover:bg-accent/20'
              }`}
              onClick={() => setShowUserMenu(false)}
            >
              <Icon name="Sparkles" size={18} />
              <span>Special Request</span>
            </Link>

            {/* Notification Bell */}
            {isAuthenticated && (
              <div className="relative">
                <button
                  onClick={toggleNotifications}
                  className="relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-colors"
                >
                  <Icon name="Bell" size={22} className="text-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationPanel />
              </div>
            )}

            {/* Cart Button */}
            <button
              onClick={openCart}
              className="relative flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ShoppingCart" size={22} className="text-foreground" />
              {getCartItemsCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getCartItemsCount()}
                </span>
              )}
            </button>

            {isAuthenticated ? (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden md:block text-sm font-medium text-foreground max-w-[120px] truncate">
                    {displayName}
                  </span>
                  <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                </button>
                
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowUserMenu(false)}
                      aria-hidden="true"
                    />
                    <div className="absolute left-4 right-4 sm:left-auto sm:right-0 top-full mt-2 w-auto sm:w-56 bg-card rounded-xl border border-border shadow-lg z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/customer-dashboard"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="LayoutDashboard" size={18} />
                          Dashboard
                        </Link>
                        <Link
                          to="/order-management"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="ShoppingBag" size={18} />
                          My Orders
                        </Link>
                        <Link
                          to="/custom-request"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="MessageSquare" size={18} />
                          Special Requests
                        </Link>
                        <Link
                          to="/payment-history"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="Receipt" size={18} />
                          Payment History
                        </Link>
                        <Link
                          to="/referral"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="Gift" size={18} />
                          Invite &amp; Earn
                        </Link>
                      </div>
                      <div className="py-1 border-t border-border">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/10 transition-colors"
                        >
                          <Icon name="LogOut" size={18} />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <button 
                  onClick={() => {
                    setShowUserMenu(false);
                    openAuthModal('signin');
                  }}
                  className="px-4 py-2 rounded-md font-medium text-primary bg-white border border-primary hover:bg-primary/10 transition"
                >
                  Sign In
                </button>
                <button 
                  onClick={() => {
                    setShowUserMenu(false);
                    openAuthModal('signup');
                  }}
                  className="px-4 py-2 rounded-md font-semibold text-white bg-primary hover:bg-primary/90 transition"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>

          <button
            className="header-mobile-toggle"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </header>
      
      {/* Mobile Menu - single header: only close button (main header with hamburger stays visible) */}
      <div className={`header-mobile-menu ${!isMobileMenuOpen ? 'closed' : ''}`}>
        <div className="header-mobile-menu-header justify-end">
          <button
            onClick={handleMobileMenuClose}
            aria-label="Close mobile menu"
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* User Info in Mobile */}
        {isAuthenticated && (
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-foreground">{displayName}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        <nav className="header-mobile-menu-nav">
          {/* Referral - Mobile */}
          <Link
            to="/referral"
            className={`header-mobile-menu-item ${isActive('/referral') ? 'active' : ''}`}
            onClick={() => {
              handleMobileMenuClose();
              setShowUserMenu(false);
            }}
          >
            <Icon name="Gift" size={24} />
            <span>Invite &amp; Earn</span>
          </Link>
          {/* Special Request - Mobile */}
          <Link
            to="/custom-request"
            className={`header-mobile-menu-item ${isActive('/custom-request') ? 'active' : ''}`}
            onClick={() => {
              handleMobileMenuClose();
              setShowUserMenu(false);
            }}
          >
            <Icon name="Sparkles" size={24} />
            <span>Special Request</span>
          </Link>
          
          {isAuthenticated && (
            <>
              <Link
                to="/customer-dashboard"
                className={`header-mobile-menu-item ${isActive('/customer-dashboard') ? 'active' : ''}`}
                onClick={handleMobileMenuClose}
              >
                <Icon name="LayoutDashboard" size={24} />
                <span>Dashboard</span>
              </Link>
              <Link
                to="/order-management"
                className={`header-mobile-menu-item ${isActive('/order-management') ? 'active' : ''}`}
                onClick={handleMobileMenuClose}
              >
                <Icon name="ShoppingBag" size={24} />
                <span>My Orders</span>
              </Link>
              <Link
                to="/payment-history"
                className={`header-mobile-menu-item ${isActive('/payment-history') ? 'active' : ''}`}
                onClick={handleMobileMenuClose}
              >
                <Icon name="Receipt" size={24} />
                <span>Payment History</span>
              </Link>
            </>
          )}
        </nav>

        {/* Mobile Auth Buttons */}
        <div className="px-4 py-4 border-t border-border mt-auto">
          {isAuthenticated ? (
            <button
              onClick={() => {
                  handleSignOut();
                  handleMobileMenuClose();
                }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-error/10 text-error font-medium"
            >
              <Icon name="LogOut" size={20} />
              Sign Out
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  openAuthModal('signin');
                  handleMobileMenuClose();
                }}
                className="w-full px-4 py-3 rounded-lg font-medium text-primary bg-white border border-primary"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  openAuthModal('signup');
                  handleMobileMenuClose();
                }}
                className="w-full px-4 py-3 rounded-lg font-semibold text-white bg-primary"
              >
                Sign Up
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;